import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Grid,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Search, Clear, Download, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Layout from '../../components/Layout';
import { verificationAPI, pricingAPI, creditAPI, paymentAPI, authAPI } from '../../services/api';
import { SessionManager } from '../../utils/sessionManager';

const Reports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [activeTab, setActiveTab] = useState(0);
  const [apiCalls, setApiCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    product: 'All',
    date: ''
  });

  // Credit Transfers state
  const [transferLogs, setTransferLogs] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferPage, setTransferPage] = useState(0);
  const [transferRowsPerPage, setTransferRowsPerPage] = useState(10);
  const [transferTotal, setTransferTotal] = useState(0);

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoicePage, setInvoicePage] = useState(0);
  const [invoiceRowsPerPage, setInvoiceRowsPerPage] = useState(10);
  const [userProfile, setUserProfile] = useState(null);
  const [gstRate, setGstRate] = useState(18);

  const userRole = SessionManager.getUserRole();

  useEffect(() => {
    fetchVerificationTypes();
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 1 && userRole === 'Admin' && transferLogs.length === 0) {
      fetchTransferLogs();
    }
    if (activeTab === 1 && userRole !== 'Admin' && invoices.length === 0) {
      fetchInvoices();
    }
  }, [activeTab]);

  const fetchVerificationTypes = async () => {
    try {
      const response = await pricingAPI.getAll();
      if (response.success) {
        setVerificationTypes(response.data);
      }
    } catch (error) {
      // console.error('Error fetching verification types:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await verificationAPI.getHistory(1000, 0);
      setApiCalls(response.data || []);
      setFilteredCalls(response.data || []);
    } catch (error) {
      // console.error('Error fetching data:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransferLogs = async () => {
    setTransferLoading(true);
    try {
      const response = await creditAPI.getTransferLogs(500, 0);
      if (response.success) {
        setTransferLogs(response.data.transfers || []);
        setTransferTotal(response.data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to load credit transfer logs');
    } finally {
      setTransferLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setInvoiceLoading(true);
    try {
      const [historyRes, profileRes, gstRes] = await Promise.all([
        paymentAPI.getHistory(),
        authAPI.getProfile(),
        paymentAPI.getGSTRate().catch(() => ({ data: { gst: 18 } }))
      ]);
      const paidInvoices = (historyRes.data || []).filter(p => p.status === 'paid');
      setInvoices(paidInvoices);
      setUserProfile(profileRes.data);
      setGstRate(gstRes.data.gst);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const generateInvoicePDF = (invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth / 2, 25, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('KYC Suite', pageWidth / 2, 33, { align: 'center' });
    doc.setFontSize(9);
    doc.text('DeepX Technologies Pvt. Ltd.', pageWidth / 2, 39, { align: 'center' });

    // Line
    doc.setDrawColor(200);
    doc.line(20, 44, pageWidth - 20, 44);

    // Invoice details
    const invoiceDate = new Date(invoice.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    const invoiceNo = `INV-${invoice.orderId.replace('order_', '').substring(0, 12).toUpperCase()}`;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice No:', 20, 54);
    doc.setFont('helvetica', 'normal');
    doc.text(invoiceNo, 55, 54);

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', 20, 61);
    doc.setFont('helvetica', 'normal');
    doc.text(invoiceDate, 55, 61);

    doc.setFont('helvetica', 'bold');
    doc.text('Payment ID:', 20, 68);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.paymentId || '–', 55, 68);

    // Bill To
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 120, 54);
    doc.setFont('helvetica', 'normal');
    if (userProfile) {
      doc.text(`${userProfile.firstName} ${userProfile.lastName}`, 120, 61);
      doc.text(userProfile.companyName || '', 120, 68);
      doc.text(userProfile.email || '', 120, 75);
    }

    // Table
    const baseAmount = invoice.credits * invoice.rate;
    const gstAmount = baseAmount * (gstRate / 100);
    const totalAmount = invoice.amount;

    autoTable(doc, {
      startY: 85,
      head: [['Description', 'Credits', 'Rate (Rs)', 'Amount (Rs)']],
      body: [
        [`Credit Purchase`, `${invoice.credits}`, `${invoice.rate.toFixed(2)}`, `${baseAmount.toFixed(2)}`],
        [`GST (${gstRate}%)`, '', '', `${gstAmount.toFixed(2)}`],
      ],
      foot: [['', '', 'Total', `Rs ${totalAmount.toFixed(2)}`]],
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 6 },
      columnStyles: {
        0: { cellWidth: 80 },
        3: { halign: 'right' }
      }
    });

    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('This is a computer-generated invoice and does not require a signature.', pageWidth / 2, doc.internal.pageSize.getHeight() - 15, { align: 'center' });

    doc.save(`Invoice_${invoiceNo}.pdf`);
    toast.success('Invoice downloaded');
  };

  const formatIST = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const handleSearch = () => {
    let filtered = [...apiCalls];

    // Filter by product
    if (filters.product !== 'All') {
      filtered = filtered.filter(call => call.type === filters.product);
    }

    // Filter by date
    if (filters.date) {
      filtered = filtered.filter(call => {
        const callDate = new Date(call.date).toISOString().split('T')[0];
        return callDate === filters.date;
      });
    }

    setFilteredCalls(filtered);
    setPage(0); // Reset to first page
    toast.success(`Found ${filtered.length} records`);
  };

  const handleClear = () => {
    setFilters({ product: 'All', date: '' });
    setFilteredCalls(apiCalls);
    setPage(0);
    toast.info('Filters cleared');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const paginatedData = filteredCalls.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Mobile Card Component
  const MobileCard = ({ call, index }) => (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: 2,
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          {/* Header Row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              #{page * rowsPerPage + index + 1}
            </Typography>
            <Chip
              label={call.success ? 'Success' : 'Failed'}
              color={call.success ? 'success' : 'error'}
              size="small"
            />
          </Box>

          {/* Product Name */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.5 }}
            >
              Product
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {call.type}
            </Typography>
          </Box>

          <Divider />

          {/* Status & Date Row */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                HTTP Status
              </Typography>
              <Chip
                label={call.status}
                color={
                  call.status >= 200 && call.status < 300 ? 'success' : 'error'
                }
                variant="outlined"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                Amount
              </Typography>
              <Typography variant="body1" fontWeight={700} color="primary.main">
                ₹{call.amount}
              </Typography>
            </Grid>
          </Grid>

          <Divider />

          {/* Date & Time */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                Date
              </Typography>
              <Typography variant="body2">
                {new Date(call.date).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                Time
              </Typography>
              <Typography variant="body2">
                {new Date(call.date).toLocaleTimeString()}
              </Typography>
            </Grid>
          </Grid>

          {/* Error Details (if any) */}
          {call.errorDetails && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Error Details
                </Typography>
                <Typography variant="body2" color="error.main">
                  {call.errorDetails}
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  // Tablet Card Component
  const TabletCard = ({ call, index }) => (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid item xs={12} sm={6}>
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                >
                  #{page * rowsPerPage + index + 1}
                </Typography>
                <Chip
                  label={call.success ? 'Success' : 'Failed'}
                  color={call.success ? 'success' : 'error'}
                  size="small"
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Product
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {call.type}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  HTTP Status
                </Typography>
                <Chip
                  label={call.status}
                  color={
                    call.status >= 200 && call.status < 300
                      ? 'success'
                      : 'error'
                  }
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Stack>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} sm={6}>
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Amount
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  ₹{call.amount}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Date & Time
                </Typography>
                <Typography variant="body2">
                  {new Date(call.date).toLocaleDateString()}{' '}
                  {new Date(call.date).toLocaleTimeString()}
                </Typography>
              </Box>

              {call.errorDetails && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    Error Details
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    {call.errorDetails}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
          Reports
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          View API call history and credit transfer logs
        </Typography>

        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="API Call History" />
            {userRole === 'Admin' && <Tab label="Credit Transfers" />}
            {userRole !== 'Admin' && <Tab label="Invoices" />}
          </Tabs>
        </Paper>

        {/* Tab: Invoices (clients only) */}
        {activeTab === 1 && userRole !== 'Admin' && (
          <>
            {invoiceLoading ? (
              <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              </Paper>
            ) : (
              <>
                {isMobile ? (
                  <Box>
                    {invoices.length === 0 ? (
                      <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          No invoices found. Invoices are generated when you purchase credits.
                        </Typography>
                      </Paper>
                    ) : (
                      invoices
                        .slice(invoicePage * invoiceRowsPerPage, invoicePage * invoiceRowsPerPage + invoiceRowsPerPage)
                        .map((inv, index) => (
                        <Card key={inv.orderId} sx={{ mb: 1.5, borderRadius: 2, boxShadow: 2 }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                  #{invoicePage * invoiceRowsPerPage + index + 1}
                                </Typography>
                                <Chip label="Paid" color="success" size="small" />
                              </Box>
                              <Divider />
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Invoice No</Typography>
                                <Typography variant="body2" fontWeight={600}>INV-{inv.orderId.replace('order_', '').substring(0, 12).toUpperCase()}</Typography>
                              </Box>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" display="block">Credits</Typography>
                                  <Typography variant="body2">{inv.credits}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" display="block">Amount</Typography>
                                  <Typography variant="body2" fontWeight={700} color="primary.main">₹{inv.amount.toFixed(2)}</Typography>
                                </Grid>
                              </Grid>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Date</Typography>
                                <Typography variant="body2">{new Date(inv.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Download />}
                                  onClick={() => generateInvoicePDF(inv)}
                                >
                                  Download
                                </Button>
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Box>
                ) : (
                  <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: 3 }}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Invoice No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Date (IST)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Credits</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Rate</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Amount (incl. GST)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Payment ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoices.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                <Typography variant="body1" color="text.secondary">
                                  No invoices found. Invoices are generated when you purchase credits.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            invoices
                              .slice(invoicePage * invoiceRowsPerPage, invoicePage * invoiceRowsPerPage + invoiceRowsPerPage)
                              .map((inv, index) => (
                              <TableRow key={inv.orderId} hover>
                                <TableCell>{invoicePage * invoiceRowsPerPage + index + 1}</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>INV-{inv.orderId.replace('order_', '').substring(0, 12).toUpperCase()}</TableCell>
                                <TableCell>{new Date(inv.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
                                <TableCell>{inv.credits}</TableCell>
                                <TableCell>₹{inv.rate}</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>₹{inv.amount.toFixed(2)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{inv.paymentId || '–'}</TableCell>
                                <TableCell>
                                  <Tooltip title="Download Invoice PDF">
                                    <IconButton size="small" color="primary" onClick={() => generateInvoicePDF(inv)}>
                                      <Download />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
                {invoices.length > 0 && (
                  <Paper sx={{ mt: 2, borderRadius: 2 }}>
                    <TablePagination
                      rowsPerPageOptions={[10]}
                      component="div"
                      count={invoices.length}
                      rowsPerPage={invoiceRowsPerPage}
                      page={invoicePage}
                      onPageChange={(e, newPage) => setInvoicePage(newPage)}
                      onRowsPerPageChange={() => {}}
                    />
                  </Paper>
                )}
              </>
            )}
          </>
        )}

        {/* Tab: Credit Transfers */}
        {activeTab === 1 && userRole === 'Admin' && (
          <>
            {transferLoading ? (
              <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              </Paper>
            ) : (
              <>
                {isMobile ? (
                  <Box>
                    {transferLogs.length === 0 ? (
                      <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          No credit transfers found
                        </Typography>
                      </Paper>
                    ) : (
                      transferLogs
                        .slice(transferPage * transferRowsPerPage, transferPage * transferRowsPerPage + transferRowsPerPage)
                        .map((log, index) => (
                        <Card key={log.id} sx={{ mb: 1.5, borderRadius: 2, boxShadow: 2 }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                  #{transferPage * transferRowsPerPage + index + 1}
                                </Typography>
                                <Chip label={`₹${log.amount}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                              </Box>
                              <Divider />
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Admin</Typography>
                                <Typography variant="body2" fontWeight={600}>{log.admin.name}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Client</Typography>
                                <Typography variant="body2" fontWeight={600}>{log.client.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{log.client.companyName || log.client.email}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Date & Time (IST)</Typography>
                                <Typography variant="body2">{formatIST(log.createdAt)}</Typography>
                              </Box>
                              {log.balanceAfter !== null && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">Balance After</Typography>
                                  <Typography variant="body2">₹{log.balanceAfter}</Typography>
                                </Box>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Box>
                ) : (
                  <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: 3 }}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Admin</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Client</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Company</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Credits Added</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Balance After</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Date & Time (IST)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transferLogs.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                <Typography variant="body1" color="text.secondary">
                                  No credit transfers found
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            transferLogs
                              .slice(transferPage * transferRowsPerPage, transferPage * transferRowsPerPage + transferRowsPerPage)
                              .map((log, index) => (
                              <TableRow key={log.id} hover>
                                <TableCell>{transferPage * transferRowsPerPage + index + 1}</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>{log.admin.name}</TableCell>
                                <TableCell>{log.client.name}</TableCell>
                                <TableCell>{log.client.companyName || '-'}</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>₹{log.amount}</TableCell>
                                <TableCell>{log.balanceAfter !== null ? `₹${log.balanceAfter}` : '-'}</TableCell>
                                <TableCell>{formatIST(log.createdAt)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
                <Paper sx={{ mt: 2, borderRadius: 2 }}>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={transferLogs.length}
                    rowsPerPage={transferRowsPerPage}
                    page={transferPage}
                    onPageChange={(e, newPage) => setTransferPage(newPage)}
                    onRowsPerPageChange={(e) => { setTransferRowsPerPage(parseInt(e.target.value, 10)); setTransferPage(0); }}
                  />
                </Paper>
              </>
            )}
          </>
        )}

        {/* Tab: API Call History */}
        {activeTab === 0 && (
        <>
        {/* Filter Section */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Product"
                value={filters.product}
                onChange={(e) => setFilters({ ...filters, product: e.target.value })}
                size="small"
              >
                <MenuItem value="All">All Products</MenuItem>
                {verificationTypes.map((type) => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<Search />}
                onClick={handleSearch}
                size="medium"
                disabled={loading}
                sx={{ height: '40px', textTransform: 'none' }}
              >
                Search
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Clear />}
                onClick={handleClear}
                size="medium"
                disabled={loading}
                sx={{ height: '40px', textTransform: 'none' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Loading State */}
        {loading ? (
          <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          </Paper>
        ) : (
          <>
            {/* Mobile View (xs) */}
            {isMobile ? (
              <Box>
                {paginatedData.length === 0 ? (
                  <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No records found matching your criteria
                    </Typography>
                  </Paper>
                ) : (
                  paginatedData.map((call, index) => (
                    <MobileCard key={call.id} call={call} index={index} />
                  ))
                )}
              </Box>
            ) : isTablet ? (
              /* Tablet View (sm to md) */
              <Box>
                {paginatedData.length === 0 ? (
                  <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No records found matching your criteria
                    </Typography>
                  </Paper>
                ) : (
                  paginatedData.map((call, index) => (
                    <TabletCard key={call.id} call={call} index={index} />
                  ))
                )}
              </Box>
            ) : (
              /* Desktop Table View (lg and above) */
              <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: 3 }}>
                <TableContainer
                  sx={{
                    maxHeight: 600,
                    width: '100%',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#ccc',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#999',
                      },
                    },
                  }}
                >
                  <Table
                    stickyHeader
                    size="small"
                    sx={{
                      minWidth: 1100,
                      '& .MuiTableCell-root': {
                        whiteSpace: 'nowrap',
                        px: 2,
                        py: 1.5,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Product Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Http Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Success</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Log Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Error Details</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Request Time</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Response Time</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                            <Typography variant="body1" color="text.secondary">
                              No records found matching your criteria
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedData.map((call, index) => (
                          <TableRow key={call.id} hover>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{call.type}</TableCell>
                            <TableCell>
                              <Chip
                                label={call.status}
                                color={call.status >= 200 && call.status < 300 ? 'success' : 'error'}
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={call.success ? 'Success' : 'Failed'}
                                color={call.success ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{new Date(call.date).toLocaleDateString()}</TableCell>
                            <TableCell sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {call.errorDetails || '-'}
                            </TableCell>
                            <TableCell>{new Date(call.date).toLocaleTimeString()}</TableCell>
                            <TableCell>{new Date(call.date).toLocaleTimeString()}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>₹{call.amount}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Pagination */}
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredCalls.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  '.MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    minHeight: { xs: 'auto', sm: '52px' },
                  },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  },
                }}
              />
            </Paper>
          </>
        )}
        </>
        )}
      </Container>
    </Layout>
  );
};

export default Reports;
